import { IOrderWorkLogsHook, IWorkLogTotal } from '@models';
import { OrderWorkLog } from '@services';
import { useStore } from '@stores';
import { workLogMinutes } from '@utils';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

const WHOLE_JOB = 'Whole job';

const sumBy = <TKey>(
  logs: OrderWorkLog[],
  now: dayjs.Dayjs,
  keyOf: (log: OrderWorkLog) => TKey,
  labelOf: (log: OrderWorkLog) => string
): IWorkLogTotal<TKey>[] => {
  const totals = new Map<TKey, IWorkLogTotal<TKey>>();
  logs.forEach((log) => {
    const key = keyOf(log);
    const minutes = workLogMinutes(log, now);
    const current = totals.get(key);
    if (current) {
      current.minutes += minutes;
    } else {
      totals.set(key, { key, label: labelOf(log), minutes });
    }
  });
  return Array.from(totals.values()).sort((a, b) => b.minutes - a.minutes);
};

export const useOrderWorkLogs = (orderId: string | undefined): IOrderWorkLogsHook => {
  const { orderStore } = useStore();
  const { orderWorkLogs, orderWorkLogsLoading } = orderStore;

  useEffect(() => {
    orderId && orderStore.getOrderWorkLogs(orderId);

    return () => {
      orderStore.resetOrderWorkLogs();
    };
  }, [orderStore, orderId]);

  // A job carries a handful of entries, so the totals are recomputed per render rather than memoised.
  const dataSource = Array.from(orderWorkLogs.values());
  const hasOpen = dataSource.some((log) => !log.workedTo);

  // Open entries keep counting, so tick once a minute while any exist.
  const [now, setNow] = useState(() => dayjs());
  useEffect(() => {
    if (!hasOpen) return undefined;
    setNow(dayjs());
    const id = setInterval(() => setNow(dayjs()), 60000);
    return () => clearInterval(id);
  }, [hasOpen]);

  return {
    loading: orderWorkLogsLoading,
    dataSource,
    hasOpen,
    totalMinutes: dataSource.reduce((acc, log) => acc + workLogMinutes(log, now), 0),
    minutesByTechnician: sumBy(
      dataSource,
      now,
      (log) => log.userId,
      (log) => log.user?.fullname ?? `#${log.userId}`
    ),
    minutesByService: sumBy(
      dataSource,
      now,
      (log) => log.serviceId ?? null,
      (log) => log.serviceTitle ?? WHOLE_JOB
    ),
  };
};
